import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../contexts/ApiContext";
import { cX, cY, MW, MH, VG, HG, PAD } from "../../utils/bracketLogic";
import {
  isDoubleResults,
  rebuildDoubleFromResults,
} from "../../utils/doubleElimination";
import DoubleBracketCanvas from "../bracket/DoubleBracketCanvas";
import ExportPdfButton from "../bracket/ExportPdfButton";
import RecordModal from "../players/RecordModal";
import "../../styles/crud.css";
import "./TournamentsPage.css";

const EMPTY_FORM = { name: "", status: 0 };

const STATUS_LABELS = {
  0: "Not Started",
  1: "In Progress",
  2: "Completed",
};

const CW = 160;

function buildRoundsFromResults(results, totalPlayers) {
  if (!results || results.length === 0) return null;
  const numRounds = Math.ceil(Math.log2(totalPlayers));
  const size = Math.pow(2, numRounds);

  const rounds = Array.from({ length: numRounds }, (_, r) =>
    Array.from({ length: size >> (r + 1) }, () => ({
      team1: null,
      team2: null,
      winner: null,
      s1: null,
      s2: null,
    })),
  );

  const playedRound1 = new Set();

  results.forEach(
    ({ winnerName, loserName, roundNumber, matchIndex, winnerScore, loserScore }) => {
      if (roundNumber >= numRounds) return;
      if (matchIndex >= rounds[roundNumber].length) return;
      const match = rounds[roundNumber][matchIndex];
      match.team1 = loserName;
      match.team2 = winnerName;
      match.winner = winnerName;
      match.s1 = loserScore ?? null;
      match.s2 = winnerScore ?? null;
    if (roundNumber === 0) {
      playedRound1.add(winnerName);
      playedRound1.add(loserName);
    }
    if (roundNumber + 1 < numRounds) {
      const nextMatch = rounds[roundNumber + 1][matchIndex >> 1];
      if (matchIndex % 2 === 0) nextMatch.team1 = winnerName;
      else nextMatch.team2 = winnerName;
    }
  });

  if (numRounds >= 2) {
    rounds[1].forEach((match, nm) => {
      [match.team1, match.team2].forEach((player) => {
        if (!player || playedRound1.has(player)) return;
        const feeder = [nm * 2, nm * 2 + 1].find(
          (idx) => !rounds[0][idx].winner,
        );
        if (feeder !== undefined) {
          rounds[0][feeder].team1 = player;
          rounds[0][feeder].team2 = null;
          rounds[0][feeder].winner = player;
        }
      });
    });
  }

  return rounds;
}

function ReadOnlySlot({ team, isWinner, isBye, onSelect, score }) {
  const cls = ["tn-slot", isWinner ? "w" : "", !team ? "bye" : ""]
    .join(" ")
    .trim();
  return (
    <div className={cls}>
      {team ? (
        <span
          className="tn-sn tn-sn-link"
          onClick={() => onSelect(team)}
          title={`View ${team}'s record`}
        >
          {team}
        </span>
      ) : (
        <span className="tn-sn">{isBye ? "BYE" : "TBD"}</span>
      )}
      {score != null && <span className="tn-score">{score}</span>}
      {isWinner && <span className="tn-arr">▶</span>}
    </div>
  );
}

function BracketModal({ tournament, onClose }) {
  const { matchResultsApi } = useApi();
  const totalPlayers = tournament.playerIds?.length ?? 0;
  const [recordPlayer, setRecordPlayer] = useState(null);
  const stageRef = useRef(null);

  const {
    data: results,
    isLoading: loading,
    isError,
  } = useQuery({
    queryKey: ["matchResults", "byTournament", tournament.id],
    queryFn: () => matchResultsApi.getByTournament(tournament.id),
  });

  const isDouble = results ? isDoubleResults(results) : false;
  let rounds = null;
  let doubleState = null;
  let error = "";
  if (isError) {
    error = "Could not load bracket data.";
  } else if (results) {
    if (results.length === 0) {
      error = "No match results found.";
    } else if (isDouble) {
      doubleState = rebuildDoubleFromResults(results, totalPlayers);
      if (!doubleState) error = "Could not reconstruct bracket.";
    } else {
      const rebuilt = buildRoundsFromResults(results, totalPlayers);
      if (!rebuilt) error = "Could not reconstruct bracket.";
      else rounds = rebuilt;
    }
  }

  const numR = rounds?.length ?? 0;
  const svgW = rounds ? PAD + numR * (MW + HG) + CW + PAD : 0;
  const svgH = rounds ? PAD + rounds[0].length * (MH + VG) - VG + PAD : 0;
  const champX = rounds ? cX(numR) : 0;
  const champY = rounds ? cY(numR - 1, 0) : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box bracket-modal-box"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{tournament.name}</h2>
          <div className="modal-header-actions">
            {(doubleState || rounds) && (
              <ExportPdfButton
                targetRef={stageRef}
                filename={tournament.name}
                className="crud-btn-ghost"
              />
            )}
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        {loading ? (
          <p className="crud-loading">Loading bracket...</p>
        ) : error ? (
          <p className="tn-err">{error}</p>
        ) : doubleState ? (
          <DoubleBracketCanvas
            state={doubleState}
            onSelectPlayer={(name) => setRecordPlayer({ name })}
            stageRef={stageRef}
          />
        ) : (
          <div className="tn-bscroll">
            <div className="tn-rlabels" style={{ marginLeft: PAD }}>
              {rounds.map((round, r) => {
                const fromEnd = numR - 1 - r;
                let label = `Round of ${round.length * 2}`;
                if (fromEnd === 0) label = "Final";
                else if (fromEnd === 1) label = "Semifinal";
                else if (fromEnd === 2) label = "Quarterfinal";
                return (
                  <div key={r} className="tn-rlabel" style={{ width: MW + HG }}>
                    {label}
                  </div>
                );
              })}
              <div className="tn-rlabel" style={{ width: CW }}>
                Champion
              </div>
            </div>
            <div
              ref={stageRef}
              style={{ position: "relative", width: svgW, height: svgH }}
            >
              <svg
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                }}
                width={svgW}
                height={svgH}
              >
                {rounds.slice(0, -1).map((round, r) =>
                  round.map((match, m) => {
                    const x1 = cX(r) + MW,
                      y1 = cY(r, m);
                    const x2 = cX(r + 1),
                      y2 = cY(r + 1, m >> 1);
                    const mx = x1 + HG / 2;
                    return (
                      <polyline
                        key={`${r}-${m}`}
                        points={`${x1},${y1} ${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                        fill="none"
                        stroke={
                          match.winner
                            ? "var(--color-won-border)"
                            : "var(--color-border)"
                        }
                        strokeWidth={2}
                      />
                    );
                  }),
                )}
                {(() => {
                  const x1 = cX(numR - 1) + MW;
                  const mx = x1 + HG / 2;
                  return (
                    <polyline
                      points={`${x1},${champY} ${mx},${champY} ${mx},${champY} ${champX},${champY}`}
                      fill="none"
                      stroke="var(--color-won-border)"
                      strokeWidth={2}
                    />
                  );
                })()}
              </svg>
              {rounds.map((round, r) =>
                round.map((match, m) => {
                  const { team1, team2, winner } = match;
                  return (
                    <div
                      key={`${r}-${m}`}
                      className={`tn-match${winner ? " won" : ""}`}
                      style={{
                        left: cX(r),
                        top: cY(r, m) - MH / 2,
                        width: MW,
                        height: MH,
                      }}
                    >
                      <ReadOnlySlot
                        team={team1}
                        isWinner={winner === team1 && !!team1}
                        isBye={!team1 && !!winner}
                        onSelect={(name) => setRecordPlayer({ name })}
                        score={match.s1}
                      />
                      <div className="tn-divider" />
                      <ReadOnlySlot
                        team={team2}
                        isWinner={winner === team2 && !!team2}
                        isBye={!team2 && !!winner}
                        onSelect={(name) => setRecordPlayer({ name })}
                        score={match.s2}
                      />
                    </div>
                  );
                }),
              )}
              <div
                className="tn-champ-slot filled"
                style={{
                  left: champX,
                  top: champY - MH / 2,
                  width: CW,
                  height: MH,
                }}
              >
                {tournament.winner ? (
                  <span
                    className="tn-champ-name tn-sn-link"
                    onClick={() => setRecordPlayer({ name: tournament.winner })}
                    title={`View ${tournament.winner}'s record`}
                  >
                    {tournament.winner}
                  </span>
                ) : (
                  <span className="tn-champ-name">{tournament.winner}</span>
                )}
              </div>
            </div>
          </div>
        )}
        {recordPlayer && (
          <RecordModal
            player={recordPlayer}
            onClose={() => setRecordPlayer(null)}
          />
        )}
      </div>
    </div>
  );
}

function TournamentsPage() {
  const { tournamentsApi } = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [detailTournament, setDetailTournament] = useState(null);

  const {
    data: tournaments = [],
    isLoading: loading,
    isError: loadError,
  } = useQuery({
    queryKey: ["tournaments"],
    queryFn: () => tournamentsApi.getAll(),
  });

  const invalidateTournaments = () =>
    queryClient.invalidateQueries({ queryKey: ["tournaments"] });

  const saveMutation = useMutation({
    mutationFn: ({ id, data }) =>
      id ? tournamentsApi.update(id, data) : tournamentsApi.create(data),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      setEditing(null);
      setError("");
      invalidateTournaments();
    },
    onError: (e) => setError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => tournamentsApi.delete(id),
    onSuccess: invalidateTournaments,
    onError: (e) => setError(e.message),
  });

  function handleSubmit() {
    if (!form.name.trim()) return setError("Name is required.");
    // For edits, preserve the rest of the tournament (bracket state, etc.).
    const data = editingId
      ? {
          ...tournaments.find((t) => t.id === editingId),
          name: form.name.trim(),
          status: form.status,
        }
      : { name: form.name.trim() };
    saveMutation.mutate({ id: editingId, data });
  }

  function handleDelete(id) {
    deleteMutation.mutate(id);
  }

  function handleEdit(t) {
    setEditing(t.id);
    setForm({ name: t.name, status: t.status });
    setError("");
  }

  function handleCancel() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
  }

  const displayError =
    error ||
    (loadError ? "Could not load tournaments. Is the API running?" : "");

  return (
    <div className="crud-page">
      {detailTournament && (
        <BracketModal
          tournament={detailTournament}
          onClose={() => setDetailTournament(null)}
        />
      )}

      <div className="crud-header">
        <h1 className="crud-title">Tournaments</h1>
        <p className="crud-sub">
          {tournaments.length} tournament{tournaments.length !== 1 ? "s" : ""}{" "}
          created
        </p>
      </div>

      <div className="crud-form">
        <h2 className="crud-form-title">
          {editingId ? "Edit Tournament" : "Add Tournament"}
        </h2>
        <div className="crud-form-row">
          <input
            className="tn-input"
            placeholder="Tournament name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
          {editingId && (
            <select
              className="tn-input"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: parseInt(e.target.value) }))
              }
            >
              <option value={0}>Not Started</option>
              <option value={1}>In Progress</option>
              <option value={2}>Completed</option>
            </select>
          )}
          <button className="crud-btn-primary" onClick={handleSubmit}>
            {editingId ? "Save" : "Add"}
          </button>
          {editingId && (
            <button className="crud-btn-ghost" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
        {displayError && <p className="tn-err">{displayError}</p>}
      </div>

      {loading ? (
        <p className="crud-loading">Loading...</p>
      ) : tournaments.length === 0 ? (
        <p className="crud-empty">No tournaments yet. Add one above.</p>
      ) : (
        <div className="crud-table-wrap">
          <table className="crud-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Winner</th>
                <th>Runner Up</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>
                    <span className={`crud-badge crud-badge-${t.status}`}>
                      {STATUS_LABELS[t.status]}
                    </span>
                  </td>
                  <td>
                    {t.winner ? (
                      <span className="tournament-winner">{t.winner}</span>
                    ) : (
                      <span className="crud-none">—</span>
                    )}
                  </td>
                  <td>
                    {t.runnerUp ? (
                      <span>{t.runnerUp}</span>
                    ) : (
                      <span className="crud-none">—</span>
                    )}
                  </td>
                  <td className="crud-actions">
                    {t.status === 1 && (
                      <button
                        className="crud-btn-continue"
                        onClick={() => navigate(`/app/play/${t.id}`)}
                      >
                        Continue
                      </button>
                    )}
                    {t.status === 2 && (
                      <button
                        className="crud-btn-ghost"
                        onClick={() => setDetailTournament(t)}
                      >
                        View Details
                      </button>
                    )}
                    <button
                      className="crud-btn-ghost"
                      onClick={() => handleEdit(t)}
                    >
                      Edit
                    </button>
                    <button
                      className="crud-btn-danger"
                      onClick={() => handleDelete(t.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TournamentsPage;
